// src/layouts/MainLayout.jsx
import React, { useState, useMemo } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Drawer, Grid } from 'antd';
import {
    DashboardOutlined,
    ShareAltOutlined,
    WalletOutlined,
    BarChartOutlined,
    FileTextOutlined,
    SettingOutlined,
    UserOutlined,
    SafetyCertificateOutlined,
    ApiOutlined,
    BellOutlined,
    GlobalOutlined,
    GiftOutlined,
    CreditCardOutlined,
    ShopOutlined,
    PlusCircleOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';

import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import TopNav from '../components/layout/TopNavpanel';
import SiteFooter from '../components/layout/SiteFooter';
import { ROUTES } from '../constants/routes';

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

export default function MainLayout() {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const location = useLocation();
    const screens = useBreakpoint();
    // md, sm, xs => drawer. lg+ => static sider
    const isMobile = !screens.lg;

    const { user } = useAuth();
    const { isDark, isRtl } = useTheme();

    const menuItems = useMemo(
        () => [
            { key: ROUTES.DASHBOARD, icon: <DashboardOutlined />, label: <Link to={ROUTES.DASHBOARD}>Dashboard</Link> },
            {
                key: 'marketplace', icon: <ShopOutlined />, label: 'Marketplace',
                children: [
                    { key: ROUTES.MARKET, icon: <BarChartOutlined />, label: <Link to={ROUTES.MARKET}>Market</Link> },
                    { key: 'gift-cards', icon: <GiftOutlined />, label: 'Gift Cards' },
                    { key: 'cards', icon: <CreditCardOutlined />, label: 'Cards' },
                ],
            },
            {
                key: 'vouchers-group', icon: <GiftOutlined />, label: 'Vouchers',
                children: [
                    { key: ROUTES.VOUCHERS_LIST, icon: <UnorderedListOutlined />, label: <Link to={ROUTES.VOUCHERS_LIST}>My Vouchers</Link> },
                    { key: ROUTES.VOUCHERS_CREATE, icon: <PlusCircleOutlined />, label: <Link to={ROUTES.VOUCHERS_CREATE}>Create Voucher</Link> },
                    { key: ROUTES.VOUCHERS_REDEEM, icon: <PlusCircleOutlined />, label: <Link to={ROUTES.VOUCHERS_REDEEM}>Redeem Voucher</Link> },
                ],
            },
            { key: ROUTES.WALLETS, icon: <WalletOutlined />, label: <Link to={ROUTES.WALLETS}>Wallets</Link> },
            {
                key: 'orders', icon: <FileTextOutlined />, label: 'Orders',
                children: [
                    { key: ROUTES.OPEN_ORDERS, label: <Link to={ROUTES.OPEN_ORDERS}>Open Orders</Link> },
                    { key: ROUTES.ORDER_HISTORY, label: <Link to={ROUTES.ORDER_HISTORY}>Order History</Link> },
                    { key: ROUTES.TRANSACTIONS, label: <Link to={ROUTES.TRANSACTIONS}>Transactions</Link> },
                    { key: ROUTES.INVOICES, label: <Link to={ROUTES.INVOICES}>Invoices</Link> },
                ],
            },
            { key: ROUTES.KYC, icon: <SafetyCertificateOutlined />, label: <Link to={ROUTES.KYC}>KYC</Link> },
            { key: ROUTES.REFERRALS, icon: <ShareAltOutlined />, label: <Link to={ROUTES.REFERRALS}><FormattedMessage id="ref.title" defaultMessage="Referrals" /></Link> },
            {
                key: 'settings', icon: <SettingOutlined />, label: 'Settings',
                children: [
                    { key: ROUTES.PROFILE, icon: <UserOutlined />, label: <Link to={ROUTES.PROFILE}>Profile</Link> },
                    { key: ROUTES.SECURITY, icon: <SafetyCertificateOutlined />, label: <Link to={ROUTES.SECURITY}>Security</Link> },
                    { key: ROUTES.API_KEYS, icon: <ApiOutlined />, label: <Link to={ROUTES.API_KEYS}>API Keys</Link> },
                    { key: ROUTES.NOTIFICATIONS, icon: <BellOutlined />, label: <Link to={ROUTES.NOTIFICATIONS}>Notifications</Link> },
                    { key: ROUTES.PREFERENCES, icon: <GlobalOutlined />, label: <Link to={ROUTES.PREFERENCES}>Preferences</Link> },
                ],
            },
        ],
        []
    );

    const Brand = ({ collapsed: isCollapsed }) => (
        <div
            style={{
                height: 56, margin: '16px 16px 8px',
                display: 'flex', alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                gap: 10, color: 'var(--home-fg)',
                fontWeight: 800, fontSize: 20, letterSpacing: 1,
                transition: 'all 0.2s',
            }}
        >
            <div
                style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: isDark ? 'linear-gradient(135deg, #8ab4f8, #669df6)' : 'linear-gradient(135deg, #1677ff, #4096ff)',
                    display: 'grid', placeItems: 'center',
                    color: isDark ? '#121212' : '#fff',
                    fontSize: 16, flexShrink: 0,
                }}
            >
                S
            </div>
            {!isCollapsed && (
                <span><span style={{ color: 'var(--home-accent)' }}>SOHOCARD</span></span>
            )}
        </div>
    );

    const siderContent = (
        <>
            <Brand collapsed={collapsed && !isMobile} />
            <Menu
                theme={isDark ? 'dark' : 'light'}
                mode="inline"
                selectedKeys={[location.pathname]}
                defaultOpenKeys={['marketplace', 'orders', 'vouchers-group', 'settings']}
                items={menuItems}
                style={{ borderInlineEnd: 'none', background: 'transparent', color: 'var(--home-fg)' }}
                onClick={() => isMobile && setMobileOpen(false)}
            />
        </>
    );

    const macScroll = {
        scrollbarWidth: 'thin',
        scrollbarColor: isDark ? 'rgba(255,255,255,0.15) transparent' : 'rgba(0,0,0,0.15) transparent',
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <TopNav onOpenSidebar={() => setMobileOpen(true)} hideOwnDrawer={true} />

            {!isMobile && (
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={setCollapsed}
                    theme={isDark ? 'dark' : 'light'}
                    width={240}
                    collapsedWidth={72}
                    style={{
                        background: isDark ? 'var(--home-bg-soft)' : 'var(--home-card-bg)',
                        borderInlineEnd: `1px solid var(--home-border)`,
                        position: 'fixed',
                        top: 72,
                        insetInlineStart: 0,
                        height: 'calc(100vh - 72px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        ...macScroll,
                    }}
                >
                    {siderContent}
                </Sider>
            )}

            {isMobile && (
                <Drawer
                    placement={isRtl ? 'right' : 'left'}
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    closable={false}
                    width={260}
                    styles={{
                        body: {
                            padding: 0,
                            background: isDark ? 'var(--home-bg-soft)' : 'var(--home-card-bg)',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            ...macScroll,
                        },
                    }}
                >
                    {siderContent}
                </Drawer>
            )}

            <Layout
                style={{
                    marginInlineStart: isMobile ? 0 : (collapsed ? 72 : 240),
                    paddingTop: 72,
                    transition: 'margin-inline-start 0.2s',
                    background: 'var(--home-bg)',
                }}
            >
                <Content style={{ margin: isMobile ? 12 : 24, padding: 0, minHeight: 'calc(100vh - 200px)' }}>
                    <Outlet />
                </Content>
                <SiteFooter />
            </Layout>
        </Layout>
    );
}